import React, { useState, forwardRef, useImperativeHandle, useCallback, useRef, useEffect, memo } from 'react';
import styles from './modalConfirmaExclusao.module.css';

const ModalConfirmaExclusao = memo(forwardRef(({
    onConfirm,
    onCancel,
    title = 'Confirmar Exclusão',
    message = 'Tem certeza que deseja excluir? Esta ação é irreversível e todos os dados serão permanentemente removidos.',
    confirmButtonText = 'Sim, Excluir',
    cancelButtonText = 'Cancelar',
    ...rest
}, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [resolvePromise, setResolvePromise] = useState(null);
    const modalRef = useRef(null);

    const ANIMATION_DURATION = 300;

    const handleModalAction = useCallback((confirmed) => {
        setIsAnimating(true);

        setTimeout(() => {
            setIsVisible(false);
            setIsAnimating(false);
            
            // Resolve a promise com o resultado da ação
            if (resolvePromise) {
                resolvePromise(confirmed);
                setResolvePromise(null);
            }
        }, ANIMATION_DURATION);
    }, [resolvePromise]);

    const handleOverlayClick = useCallback((e) => {
        // Verifica se o clique foi diretamente no overlay
        if (e.target === e.currentTarget) {
            handleModalAction(false);
        }
    }, [handleModalAction]);

    // Adiciona listener de tecla ESC
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isVisible) {
                handleModalAction(false);
            }
        };

        if (isVisible) {
            document.addEventListener('keydown', handleEscKey);
            return () => {
                document.removeEventListener('keydown', handleEscKey);
            };
        }
    }, [isVisible, handleModalAction]);

    // Métodos públicos para o pai
    useImperativeHandle(ref, () => ({
        showModal: () => {
            setIsAnimating(true);
            setTimeout(() => {
                setIsAnimating(false);
                setIsVisible(true);
            }, ANIMATION_DURATION);

            // Retorna uma Promise que será resolvida quando o modal for fechado
            return new Promise((resolve) => {
                setResolvePromise(() => resolve);
            });
        },
        hideModal: () => handleModalAction(false)
    }), [handleModalAction]);

    // Memoized click handlers
    const handleConfirmClick = useCallback(() => {
        handleModalAction(true);
    }, [handleModalAction]);

    const handleCancelClick = useCallback(() => {
        handleModalAction(false);
    }, [handleModalAction]);

    // Early return se não estiver visível e não estiver animando
    if (!isVisible && !isAnimating) return null;

    return (
        <div 
            className={`${styles.modal_overlay} ${isAnimating ? styles.hide : styles.active}`}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            {...rest}
        >
            <div
                ref={modalRef}
                className={styles.modal}
                role="document"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modal_header}>
                    <svg
                        className={styles.warning_icon}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v7h-2zm0 8h2v2h-2z" />
                    </svg>
                    <h2
                        id="modal-title"
                        className={styles.modal_title}
                    >
                        {title}
                    </h2>
                </div>
                <div className={styles.modal_content}>
                    {message}
                </div>
                <div className={styles.modal_buttons}>
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btn_cancel}`}
                        onClick={handleCancelClick}
                    >
                        {cancelButtonText}
                    </button>
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btn_confirm}`}
                        onClick={handleConfirmClick}
                    >
                        {confirmButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
}));

ModalConfirmaExclusao.displayName = 'ModalConfirmaExclusao';

export default ModalConfirmaExclusao;